import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { inwardApi, StockListItem } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const NewInwardModal: React.FC<{ productionId: string; unitId: string; onClose: () => void; onSaved: () => void }> = ({ productionId, unitId, onClose, onSaved }) => {
  const [v, setV] = useState({ Mcs: "", LooseBags: "", UnitID: unitId, Remarks: "" });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await inwardApi.post({ ProductionID: productionId, TransactionDate: today(), ...v });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title="New Inward Entry" onClose={onClose} testId="inward-modal">
      <form onSubmit={submit} data-testid="inward-form">
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Mcs</label>
            <input className="form-control" value={v.Mcs} onChange={(e) => setV((s) => ({ ...s, Mcs: e.target.value }))} data-testid="inward-mcs" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Loose Bags</label>
            <input className="form-control" value={v.LooseBags} onChange={(e) => setV((s) => ({ ...s, LooseBags: e.target.value }))} data-testid="inward-loose-bags" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Coldstore Unit ID</label>
            <input className="form-control" value={v.UnitID} onChange={(e) => setV((s) => ({ ...s, UnitID: e.target.value }))} data-testid="inward-unit-id" /></div>
        </div>
        <div className="mb-3"><label className="form-label">Remarks</label>
          <input className="form-control" value={v.Remarks} onChange={(e) => setV((s) => ({ ...s, Remarks: e.target.value }))} data-testid="inward-remarks" /></div>
        {err && <Alert kind="danger" testId="inward-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="inward-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/ColdStore/daily-inward.php - creates `CsStockData` from a Daily Production Data
 * entry's balance; a barcode groups every item scanned together on the same pallet. */
export default function InwardPage() {
  const [params, setParams] = useSearchParams();
  const productionDate = params.get("ProductionDate") || today();
  const productionId = params.get("ProductionID") || "";
  const qc = useQueryClient();

  const productions = useQuery({ queryKey: ["coldstore", "inward-productions", productionDate], queryFn: () => inwardApi.get({ ProductionDate: productionDate }) });
  const detail = useQuery({ queryKey: ["coldstore", "inward-detail", productionId], enabled: !!productionId, queryFn: () => inwardApi.get({ ProductionID: productionId }) });
  const [showNew, setShowNew] = useState(false);
  const prod = (detail.data?.DailyProductionData || {}) as any;

  const barcode = detail.data?.StockList?.BarCodeID;
  const barcodeItems = useQuery({ queryKey: ["coldstore", "inward-barcode", barcode], enabled: !!barcode,
    queryFn: () => inwardApi.get({ BarCodeID: String(barcode) }) });

  const del = async (item: StockListItem) => {
    if (!window.confirm("Delete this Inward line?")) return;
    await inwardApi.delete({ StockID: item.StockID });
    qc.invalidateQueries({ queryKey: ["coldstore", "inward-detail", productionId] });
    qc.invalidateQueries({ queryKey: ["coldstore", "inward-barcode", barcode] });
  };

  return (
    <>
      <PageHeader title="Daily Inward" location="ColdStore" />
      <div className="card mb-3"><div className="card-body">
        <div className="row">
          <div className="col-md-3 mb-3"><label className="form-label">Production Date</label>
            <input className="form-control" type="date" value={productionDate} onChange={(e) => setParams((p) => { p.set("ProductionDate", e.target.value); return p; })} data-testid="inward-date" /></div>
          <div className="col-md-5 mb-3"><label className="form-label">Production Entry</label>
            <ErpSelect id="inward-production" value={productionId} onChange={(v) => setParams((p) => { p.set("ProductionID", v); return p; })}
              options={(productions.data?.DailyProductionList || []).map((r) => ({ value: String(r.ProductionID), label: `#${r.ProductionID} ${r.OurSupc || ""} (${r.Status === 1 ? "Inwarded" : "Pending"})` }))}
              placeholder="Select Production Entry" testId="inward-production" /></div>
        </div>
      </div></div>

      {productionId && (
        <div className="card mb-3"><div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div><strong>Balance:</strong> {prod.BalanceKgs ?? "-"} Kgs (Total: {prod.Kgs ?? "-"} Kgs)</div>
            <button className="btn btn-sm btn-success" data-testid="inward-add-new" onClick={() => setShowNew(true)}>+ New Inward Entry</button>
          </div>
          <ErpTable<StockListItem> testId="inward-table" loading={barcodeItems.isLoading}
            error={barcodeItems.isError ? errorText(barcodeItems.error) : null}
            rows={barcodeItems.data?.StockList?.Data || []}
            columns={[{ key: "StockID", title: "Stock ID" }, { key: "Variety", title: "Variety" }, { key: "Grade", title: "Grade" },
              { key: "Mcs", title: "Mcs" }, { key: "LooseBags", title: "Loose Bags" },
              { key: "Action", title: "Action", render: (r) => (
                <button className="btn btn-sm btn-outline-danger" data-testid={`inward-delete-${r.StockID}`} onClick={() => del(r)}>Delete</button>
              ) }]} />
        </div></div>
      )}
      {showNew && <NewInwardModal productionId={productionId} unitId=""
        onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ["coldstore", "inward-detail", productionId] }); qc.invalidateQueries({ queryKey: ["coldstore", "inward-productions", productionDate] }); }} />}
    </>
  );
}
